<template>
  <StepPanel>
    <ApplicationSubmitted v-if="isSuccess" />
    <div v-else class="flex flex-col gap-8">
      <div class="flex flex-col gap-4">
        <div class="flex flex-row items-center justify-between">
          <UiText as="p" class="font-normal font-archivo text-primary tracking-10p"
            :text="listingType === 'freelancer' ? 'CREATE FREELANCER LISTING' : (listingType === 'app_service_provider' ? 'CREATE APP SERVICE PARTNER LISTING' : 'CREATE AGENCY LISTING')" />
          <UiText as="p" class="font-normal font-archivo text-primary tracking-10p" text="3/3" />
        </div>
        <UiText as="h3" class="font-normal text-primary" text="You're almost done!" />
        <UiText as="p" class="text-primary"
          :text="listingType === 'freelancer' 
            ? 'Tell us a bit about your experience — your tech stack, favorite tools, and projects you\'re proud of. This will show on your profile (you can edit it anytime) and helps us verify and approve your account.'
            : listingType === 'app_service_provider'
              ? 'Tell us about your app service partner business — your team\'s skills, tech stack, and the projects you specialize in. This will appear on your profile (you can edit it anytime) and helps us verify and approve your application.'
              : 'Tell us about your agency — your team\'s skills, tech stack, and the projects you specialize in. This will appear on your agency profile (you can edit it anytime) and helps us verify and approve your application.'" />
      </div>

      <div class="flex flex-col gap-8">

        <UiFormBuilder ref="formBuilder" :schema="experienceFormSchema" :rows="rows" :initialValues="initialValues"
          scroll-to-first-error v-model="formData" @submit="submitForm">
          <template #actions>
            <div class="flex justify-between gap-4">
              <UiButton variant="outline" label="Back" @click="goToPrevious" :disabled="isSubmitting" />
              <UiButton variant="primary" label="Submit Application" type="submit" :disabled="isSubmitting" />
            </div>
          </template>
        </UiFormBuilder>
      </div>
    </div>
  </StepPanel>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import { UiFormBuilder, UiButton, UiText } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import StepPanel from "./StepPanel.vue";
import ApplicationSubmitted from "./ApplicationSubmitted.vue";
import { useRegisterStore } from "@/store/register.ts";
import { experienceFormSchema } from "./schemas/experienceForm";
import { useAlertStore } from '@/store/alert';

const router = useRouter()
const registerStore = useRegisterStore()
const alertStore = useAlertStore()

const listingType = ref<'freelancer' | 'agency' | 'app_service_provider'>('freelancer')
const isSuccess = ref<boolean>(false)
const isSubmitting = ref<boolean>(false)
const formBuilder = ref<any>(null)

const formData = shallowRef({
  bio: ''
})

const rows = computed<BuilderRowConfig[]>(() => [
  {
    columns: [
      {
        type: 'richtext',
        name: 'bio',
        label: 'Share a short bio (Recommended)',
        labelCapitalize: false,
        hint: 'Add your bio to help us decide if our network is a good fit for you and your business. You can edit your bio later.',
        props: {
          placeholder: 'Tell us about your experience...',
          rows: 4,
        }
      },
    ]
  },
]);

const initialValues = ref<any>(undefined);
const hasLoaded = ref<boolean>(false);

/**
 * Normalizes budget value to ensure it's always a number.
 * Handles edge cases where currency field might return a range object or array.
 */
const normalizeBudget = (budget: any): number | null => {
  if (budget === undefined || budget === null) {
    return null
  }

  // Already a number
  if (typeof budget === 'number') {
    return budget
  }

  // Range object with min property
  if (typeof budget === 'object' && !Array.isArray(budget) && 'min' in budget) {
    const minValue = typeof budget.min === 'number' ? budget.min : parseFloat(budget.min)
    return isNaN(minValue) ? null : minValue
  }

  // Array - use first element
  if (Array.isArray(budget) && budget.length > 0) {
    const firstValue = typeof budget[0] === 'number' ? budget[0] : parseFloat(budget[0])
    return isNaN(firstValue) ? null : firstValue
  }

  // String - try to parse
  if (typeof budget === 'string') {
    const parsed = parseFloat(budget)
    return isNaN(parsed) ? null : parsed
  }

  return null
}

const validate = () => {
  const contactInfo = JSON.parse(localStorage.getItem('contactInfo') || '{}')
  const professionalDetails = JSON.parse(localStorage.getItem('professionalDetails') || '{}')
  const listingType = localStorage.getItem('listingType')

  // Check contact info based on listing type
  if (listingType === 'agency' || listingType === 'app_service_provider') {
    if (
      !contactInfo.agencyName ||
      (listingType === 'agency' && !contactInfo.website?.trim()) ||
      !contactInfo.email ||
      !contactInfo.country ||
      !contactInfo.representativeName ||
      !contactInfo.representativeRole
    ) {
      return false
    }
  } else {
    // Freelancer validation
    if (
      !contactInfo.firstName ||
      !contactInfo.lastName ||
      !contactInfo.email ||
      !contactInfo.country
    ) {
      return false
    }
  }

  // Normalize and validate budget
  const budgetValue = normalizeBudget(professionalDetails.budget)
  if (
    (listingType === 'freelancer' && !professionalDetails.role) ||
    !professionalDetails.service ||
    budgetValue === null ||
    budgetValue <= 0
  ) {
    return false
  }

  return true
}

const submitForm = async () => {
  if (isSubmitting.value) return

  isSubmitting.value = true

  try {
    if (!validate()) {
      isSubmitting.value = false
      return
    }

    const storedType = { company_type: localStorage.getItem('listingType') }
    const contactInfo = JSON.parse(localStorage.getItem('contactInfo') || '{}')
    const professionalDetails = JSON.parse(localStorage.getItem('professionalDetails') || '{}')

    // Only include properties from experience form schema (bio only)
    const experienceData = {
      bio: (formData.value as any).bio || ''
    }

    const completeFormData = {
      ...storedType,
      ...contactInfo,
      ...professionalDetails,
      ...experienceData  // Only spread approved experience properties
    }

    // Normalize budget value before submission to ensure it's always a number
    const budgetValue = normalizeBudget(completeFormData.budget)
    
    if (budgetValue === null || budgetValue <= 0) {
      isSubmitting.value = false
      alertStore.show('Please provide a valid minimum project budget.', 'error')
      return
    }

    const payload = {
      expert_type: completeFormData.company_type,
      first_name: completeFormData.firstName || completeFormData.representativeName?.split(' ')[0],
      last_name: completeFormData.lastName || completeFormData.representativeName?.split(' ').slice(1).join(' '),
      email: completeFormData.email,
      about: completeFormData.bio,
      min_project_budget: budgetValue,
      company_type: completeFormData.company_type,
      country: completeFormData.country,
      linkedIn_url: completeFormData.linkedIn,
      partner_link_directory: completeFormData.partnerLink,
      agency_name: completeFormData.agencyName,
      representative_name: completeFormData.representativeName,
      representative_role: completeFormData.representativeRole,
      role: completeFormData.role,
      service: completeFormData.service,
      url: completeFormData.website,
      shopify_plan: completeFormData.company_type === 'app_service_provider' ? undefined : completeFormData.shopifyTier,
      agency_type_fields: completeFormData.company_type === 'app_service_provider'
        ? { app_tier: completeFormData.appTier ? 'built_for_shopify' : null }
        : undefined,
      profile: completeFormData.company_type === 'app_service_provider'
        ? { agency_type_fields: { app_tier: completeFormData.appTier ? 'built_for_shopify' : null } }
        : undefined,
    }

    const response = await registerStore.signupExpert(payload)

    if (response.data.status) {
      // Clear stored form data
      localStorage.removeItem('listingType')
      localStorage.removeItem('contactInfo')
      localStorage.removeItem('professionalDetails')
      localStorage.removeItem('experience')

      // Show success screen
      isSuccess.value = true
    }
    else {
      await handleValidationErrors(response.data.errors || {})
    }
  } catch (error: any) {
    if (error?.response?.data?.errors) {
      await handleValidationErrors(error.response.data.errors)
    } else {
      alertStore.show('An unexpected error occurred. Please try again.', 'error')
    }
  } finally {
    isSubmitting.value = false
  }
}

const handleValidationErrors = async (serverErrors: any) => {
  const fieldMapping: Record<string, string> = {
    'first_name': 'firstName',
    'last_name': 'lastName',
    'email': 'email',
    'country': 'country',
    'url': 'website',
    'agency_name': 'agencyName',
    'linkedIn_url': 'linkedIn',
    'role': 'role',
    'service': 'service',
    'min_project_budget': 'budget',
    'partner_link_directory': 'partnerLink',
    'shopify_plan': 'shopifyTier',
    'app_tier': 'appTier',
    'agency_type_fields.app_tier': 'appTier',
    'profile.agency_type_fields.app_tier': 'appTier',
    'about': 'bio'
  }

  const mappedErrors: Record<string, string> = {}

  Object.entries(serverErrors).forEach(([apiField, messages]) => {
    const localField = fieldMapping[apiField]
    if (localField) {
      const errorMessage = Array.isArray(messages) ? messages[0] : messages
      if (errorMessage) {
        mappedErrors[localField] = errorMessage
      }
    }
  })

  const contactInfoFields = ['first_name', 'last_name', 'email', 'country', 'url', 'agency_name', 'linkedIn_url']
  const professionalDetailsFields = ['role', 'service', 'min_project_budget', 'partner_link_directory', 'shopify_plan', 'app_tier', 'agency_type_fields.app_tier', 'profile.agency_type_fields.app_tier']
  const experienceFields = ['about']

  const hasContactInfoErrors = contactInfoFields.some(field => serverErrors[field])
  const hasProfessionalErrors = professionalDetailsFields.some(field => serverErrors[field])
  const hasExperienceErrors = experienceFields.some(field => serverErrors[field])

  if (hasExperienceErrors) {
    setTimeout(() => {
      if (formBuilder.value && formBuilder.value.setErrors) {
        formBuilder.value.setErrors(mappedErrors)

        setTimeout(() => {
          if (formBuilder.value && formBuilder.value.clearErrors) {
            formBuilder.value.clearErrors()
          }
        }, 3000)
      }
    }, 100)
  }

  if (hasContactInfoErrors) {
    localStorage.setItem('signupErrors', JSON.stringify(mappedErrors))
    await router.push('/expert/signup/contact-info')
    return
  }

  if (hasProfessionalErrors) {
    localStorage.setItem('signupErrors', JSON.stringify(mappedErrors))
    await router.push('/expert/signup/professional-details')
    return
  }
}

const goToPrevious = () => {
  router.push('/expert/signup/professional-details')
}

onMounted(() => {
  const type = localStorage.getItem('listingType')
  if (type === 'freelancer' || type === 'agency' || type === 'app_service_provider') {
    listingType.value = type
  } else {
    // fallback to start
    router.push('/expert/signup')
  }

  const saved = localStorage.getItem('experience')
  if (saved) {
    const savedData = JSON.parse(saved)
    // Only load properties that belong to experience form schema (bio only)
    const experienceData = {
      bio: savedData.bio || ''
    }
    initialValues.value = experienceData
    formData.value = experienceData
  }

  hasLoaded.value = true
})

watch(formData, (newValue) => {
  if (hasLoaded.value && newValue) {
    // Only save properties that belong to experience form schema (bio only)
    const experienceData = {
      bio: (newValue as any).bio || ''
    }
    localStorage.setItem('experience', JSON.stringify(experienceData))
  }
}, { deep: true })
</script>
